import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { LogIn, Loader2, Sparkles, CheckCircle2, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const DEFAULT_BENEFITS = [
  'Direct 1688 & Taobao product sourcing',
  'Professional procurement & quality checks',
  'Personal Chinese warehouse address',
  'Warehousing & free consolidation allowance',
  'Air & Sea freight forwarding to Nigeria',
  'Real-time order & shipment tracking',
];

export default function MembershipGate({ open, onClose, action = 'proceed' }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [fee, setFee] = useState({ ngn: 4800, rmb: 22 });
  const [card, setCard] = useState({ headline: 'Unlock the Full Sinolink Experience', benefits: DEFAULT_BENEFITS, note: 'One-time payment. No renewals, no hidden charges.' });

  useEffect(() => {
    if (open) {
      setAgreed(false);
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/public`)
        .then(r => r.json())
        .then(d => {
          const ngn = d.membership_fee_ngn || 4800;
          const rate = d.ngn_to_rmb || 216;
          setFee({ ngn, rmb: (ngn / rate).toFixed(2) });
          const benefits = (d.activation_benefits || []).map(b => (b || '').trim()).filter(Boolean);
          setCard({
            headline: d.activation_headline || 'Unlock the Full Sinolink Experience',
            benefits: benefits.length ? benefits : DEFAULT_BENEFITS,
            note: d.activation_note || 'One-time payment. No renewals, no hidden charges.'
          });
        }).catch(() => {});
    }
  }, [open]);

  const handleActivate = async () => {
    if (!user) {
      navigate('/register');
      onClose();
      return;
    }
    if (!agreed) {
      toast.error('Please confirm you understand the activation terms first');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/membership/activate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.status === 'already_member') {
        toast.success('Your account is already activated!');
        onClose();
        window.location.reload();
        return;
      }
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        const errMsg = data.detail || data.message || 'Payment initialization failed. Please try again.';
        setError(errMsg);
        toast.error(errMsg);
      }
    } catch {
      setError('Connection error. Please check your internet and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[360px] sm:max-w-md p-0 rounded-2xl overflow-hidden max-h-[92vh] flex flex-col" data-testid="membership-gate-modal">
        <DialogTitle className="sr-only">Activate Your Sinolink Account</DialogTitle>
        <DialogDescription className="sr-only">One-time account activation unlocks all platform features</DialogDescription>

        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 to-primary/90 px-5 py-5 text-center flex-shrink-0">
          <div className="w-11 h-11 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-2">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <h2 className="text-lg font-black text-white" data-testid="activation-headline">{card.headline}</h2>
          <p className="text-xs text-white/60 mt-1">Activate once to {action}</p>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain">
          <div className="p-5 space-y-3">
            {/* Benefits card */}
            <div className="bg-gradient-to-b from-slate-50 to-white border border-slate-200 rounded-2xl p-4 shadow-sm" data-testid="activation-benefits-card">
              <p className="text-[10px] font-bold text-primary uppercase tracking-widest mb-2.5">Account holder benefits</p>
              <div className="space-y-2">
                {card.benefits.map((b, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-slate-700 leading-snug">{b}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Fee card */}
            <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl p-4 border border-primary/10 text-center" data-testid="activation-fee-card">
              <div className="flex items-baseline justify-center gap-2">
                <span className="text-2xl font-black text-primary">₦{Number(fee.ngn).toLocaleString()}</span>
                <span className="text-xs text-slate-400">≈ ¥{fee.rmb}</span>
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5">One-time account activation fee</p>
              <p className="text-[10px] text-slate-400 mt-1">{card.note}</p>
            </div>

            {/* Understanding confirmation */}
            <label className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-colors ${agreed ? 'border-emerald-300 bg-emerald-50/60' : 'border-slate-200 bg-slate-50 hover:border-slate-300'}`}>
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[hsl(340,80%,30%)]"
                data-testid="activation-agree-checkbox"
              />
              <span className="text-[11px] text-slate-600 leading-snug">
                I understand the ₦{Number(fee.ngn).toLocaleString()} is a one-time account activation fee that unlocks full access to all Sinolink services listed above.
              </span>
            </label>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 text-xs text-red-700" data-testid="membership-error">
                {error}
              </div>
            )}

            <Button
              onClick={handleActivate}
              disabled={loading || (!!user && !agreed)}
              className="w-full h-11 bg-primary hover:bg-primary/90 font-bold rounded-xl disabled:opacity-50"
              data-testid="activate-membership-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {user ? 'Activate My Account' : 'Register & Activate'}
            </Button>
            {!user && (
              <Button variant="outline" onClick={() => { navigate('/login'); onClose(); }} className="w-full h-9 rounded-xl text-sm" data-testid="login-btn">
                <LogIn className="h-4 w-4 mr-2" /> Already have an account? Login
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
